import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import DesignEditorLoadingIndicator from '../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import DesignEditor from '../../../../components/global/DesignEditor';
import { ORIENTATION } from '../../../cards/constants';
import PrintProductGroupConfigurationItem from './components/PrintProductGroupConfigurationItem';
import PrintProductsGroupConfiguratorHeaderPane from './components/PrintProductsGroupConfiguratorHeaderPane';
import DesignEditorPreviewPane from '../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import Button from '../../../../components/global/Button';
import { getPhotoCardElements } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/helpers';
import { getBackgroundAccountCardElement } from '../../../cards/views/AccountCardConfigurator/helpers';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import {
  getCardPhotoElementsWithConfigurationFromAccountCard,
  getPrintProductItemLabel,
} from './helpers';
import useRequestConfirm from '../../../../hooks/useRequestConfirm';
import CardElementPhotoSelectionControl from '../../../cards/components/CardElementPhoto/CardElementPhotoSelectionControl';

const propTypes = {
  className: PropTypes.string,
  printProducts: PropTypes.arrayOf(
    PropTypes.shape({
      printProductId: PropTypes.string,
      printProductSizeId: PropTypes.string,
      orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
      defaultAccountCard: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  buttonText: PropTypes.node,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  allowPhotoChange: PropTypes.bool,
  // Whether the package still has items to configure outside of the print
  // products handled by this configurator (specialty / custom prints, digital
  // bundles, ROES items, ...). Drives the checkout vs next item wording.
  hasOtherConfigurableItems: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
};

const PrintProductGroupConfigurator = ({
  className,
  photo,
  onComplete,
  albumMembershipGroup,
  identity,
  printProducts,
  printBackgrounds,
  onClose,
  watermarkSettings,
  allowMultiPose,
  allowMultiIdentity,
  isAllPhotos,
  hasOtherConfigurableItems,
}) => {
  const [currentPhoto, setCurrentPhoto] = useState(photo ?? null);
  const [selectingPhoto, setSelectingPhoto] = useState(null);
  const requestConfirm = useRequestConfirm();
  const allowPhotoChange =
    allowMultiPose ||
    (isMultiPosePackageMembership(albumMembershipGroup) ?? true);
  const allowMultiIdentityPhoto =
    allowMultiIdentity ||
    (isMultiIdentityPackageMembership(albumMembershipGroup) ?? false);
  const [accountCardConfigurations, setAccountCardConfigurations] = useState(
    [],
  );
  const [activeItem, setActiveItem] = useState(0);
  const [printBackgroundModalOpen, setPrintBackgroundModalOpen] =
    useState(false);
  const [printBackgroundModalHasOpened, setPrintBackgroundModalHasOpened] =
    useState(false);
  const [lastUsedPrintBackground, setLastUsedPrintBackground] = useState();
  const printProductItem = printProducts[activeItem];
  const { printProductSizeId } = printProductItem ?? {};
  const isLastPrintProductItem = activeItem + 1 === printProducts.length;

  const classes = classNames({
    [className]: !!className,
    'print-product-configurator': true,
    'print-product-group-configurator': true,
  });

  useEffect(() => {
    if (printBackgroundModalOpen) {
      setPrintBackgroundModalHasOpened(true);
    }
  }, [printBackgroundModalOpen]);

  if (!printProducts) {
    return <DesignEditorLoadingIndicator />;
  }

  const currentConfiguration = accountCardConfigurations.find(
    ({ id: printProductItemId, printProductSize }) => {
      if (printProductItemId) {
        return (
          printProductItemId === printProductItem?.id &&
          printProductSize.id === printProductSizeId
        );
      }

      return printProductSize.id === printProductSizeId;
    },
  );

  const {
    accountCard: activeAccountCard,
    accountCardElementReferences: activeAccountCardElementReferences,
  } = currentConfiguration ?? {};

  const isMissingPhotos =
    getPhotoCardElements({
      accountCard: activeAccountCard,
      emptyOnly: true,
    }).length !== 0;

  const backgroundElement = getBackgroundAccountCardElement({
    accountCard: activeAccountCard,
  });

  const photoElements = getCardPhotoElementsWithConfigurationFromAccountCard({
    accountCard: activeAccountCard,
  }).filter(
    elem =>
      elem?.cardElement?.id !== backgroundElement?.cardElement?.id &&
      (!elem.cardElementConfiguration ||
        elem.cardElementConfiguration?.is_editable),
  );

  const [mainPhotoElement] = photoElements ?? [];

  const photoElementReference =
    activeAccountCardElementReferences?.[mainPhotoElement?.cardElement?.id];

  const canCompleteSetup = !(
    (accountCardConfigurations?.length ?? 0) === 0 ||
    accountCardConfigurations.find(config => !config.accountCard) ||
    isMissingPhotos
  );
  // Add additional check for main photo element photo. THis basically allows
  // to show / remove the backdrop button if the main photo element is not
  // a GS photo.
  const hasPrintBackgrounds =
    !!backgroundElement &&
    !!mainPhotoElement?.cardElementConfiguration?.photo?.foreground &&
    printBackgrounds?.length > 0;

  // A lone backdrop is auto-applied on load, so there is nothing to change.
  const canChangePrintBackground =
    hasPrintBackgrounds && printBackgrounds.length > 1;

  const handleNextItem = () => {
    if (!isLastPrintProductItem) {
      setActiveItem(activeItem + 1);
    } else {
      onComplete(accountCardConfigurations);
    }
  };

  const onSubmit = () => {
    if (
      !printBackgroundModalHasOpened &&
      hasPrintBackgrounds &&
      printBackgrounds?.length > 1
    ) {
      requestConfirm({
        message: (
          <Fragment>
            <h4 className="text-center">Woah!</h4>
            <p className="text-center">
              Looks like you didn&apos;t update your print backdrop. This
              backdrop cannot be updated once submitted to print, so make sure
              everything looks exactly the way you want!
            </p>
          </Fragment>
        ),

        confirmText: "I'm happy with this!",
        cancelText: 'View available backdrops',
        cancelBtnStyle: 'primary',
        onConfirm: () => {
          if (allowMultiPose && !isLastPrintProductItem) {
            setSelectingPhoto(true);
          } else {
            handleNextItem();
          }
          setPrintBackgroundModalHasOpened(true);
        },
        onCancel: () => {
          setPrintBackgroundModalHasOpened(true);
          setPrintBackgroundModalOpen(true);
        },
      });

      return;
    }

    if (allowMultiPose && !isLastPrintProductItem) {
      setSelectingPhoto(true);
    } else {
      handleNextItem();
    }
  };

  const continueLabel = (() => {
    if (!isLastPrintProductItem) {
      return `let's configure the next product`;
    }

    // The package may still hold items this configurator doesn't handle, so
    // we can only send the consumer to checkout once nothing else is left.
    return hasOtherConfigurableItems
      ? `let's configure the next item`
      : 'go to checkout';
  })();

  const title = getPrintProductItemLabel({ printProducts, printProductItem });
  const nextPrintProductLabel = getPrintProductItemLabel({
    printProducts,
    printProductItem: printProducts[activeItem + 1],
  });

  return (
    <DesignEditor className={classes}>
      <PrintProductsGroupConfiguratorHeaderPane
        onClose={onClose}
        activeItem={activeItem}
        printProductItem={printProductItem}
        totalAmount={printProducts.length}
      />

      <DesignEditorPreviewPane className="print-product-group-configurator-preview-pane">
        <h4 className="my-1 text-center">{title}</h4>
        <div className="print-product-group-actions-container">
          {photoElementReference && photoElements?.length === 1 && (
            <Fragment>
              {photoElementReference?.onPhotoChange && allowPhotoChange && (
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  className="text-extra-bold"
                  onClick={() => {
                    photoElementReference.onPhotoChange();
                  }}
                >
                  Change Photo
                </Button>
              )}
              {photoElementReference?.onAdjustAndCrop && (
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  className="text-extra-bold"
                  onClick={() => {
                    photoElementReference.onAdjustAndCrop();
                  }}
                >
                  Adjust &amp; Crop
                </Button>
              )}
            </Fragment>
          )}
          {canChangePrintBackground && (
            <Button
              btnStyle="link"
              btnSize="sm"
              className="text-extra-bold"
              onClick={() => setPrintBackgroundModalOpen(true)}
            >
              {backgroundElement?.cardElementConfiguration?.asset_image?.id
                ? 'Change Backdrop'
                : 'Add Backdrop'}
            </Button>
          )}
        </div>
        <PrintProductGroupConfigurationItem
          key={printProductItem?.id ?? printProductItem?.printProductSizeId}
          photo={currentPhoto}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
          printBackgroundModalOpen={printBackgroundModalOpen}
          lastUsedPrintBackground={lastUsedPrintBackground}
          setLastUsedPrintBackground={setLastUsedPrintBackground}
          setPrintBackgroundModalOpen={setPrintBackgroundModalOpen}
          allowPhotoChange={allowPhotoChange}
          allowMultiIdentityPhoto={allowMultiIdentityPhoto}
          onAccountCardUpdated={({
            printProduct,
            printProductSize,
            accountCard,
            accountCardElementReferences,
          }) => {
            if (!printProductSize) {
              return;
            }

            const updatedConfig = {
              id: printProductItem?.id,
              printProduct,
              printProductSize,
              accountCard,
              accountCardElementReferences,
            };

            const activeConfiguration = accountCardConfigurations.find(
              config =>
                (config?.id && config.id === printProductItem?.id) ||
                (!config.id &&
                  config.printProductSize?.id === printProductSize?.id),
            );

            if (activeConfiguration) {
              setAccountCardConfigurations(current => [
                ...current.filter(
                  config =>
                    (config?.id && config.id !== printProductItem?.id) ||
                    (!config.id &&
                      config.printProductSize?.id !== printProductSize?.id),
                ),
                updatedConfig,
              ]);
            } else {
              setAccountCardConfigurations(current => [
                ...current,
                updatedConfig,
              ]);
            }
          }}
          watermarkSettings={watermarkSettings}
          isAllPhotos={isAllPhotos}
          {...printProductItem}
        />
        {canCompleteSetup && (
          <div className="print-product-group-configurator-continue-wrap">
            {canCompleteSetup && (
              <Button
                btnBlock
                btnSize="lg"
                onClick={onSubmit}
                btnStyle="primary"
                data-testid="print-product-group-configurator-continue"
                className="print-product-group-configurator-continue"
              >
                I&apos;m happy, {continueLabel}.
              </Button>
            )}
          </div>
        )}
      </DesignEditorPreviewPane>
      <CardElementPhotoSelectionControl
        forceOpen={selectingPhoto}
        title={`Pick a photo for your ${
          nextPrintProductLabel || 'next product'
        }`}
        onClose={() => setSelectingPhoto(false)}
        onRecordChange={matchedPhoto => {
          if (matchedPhoto?.__typename === 'Photo') {
            setCurrentPhoto(matchedPhoto);
          } else {
            setCurrentPhoto(matchedPhoto.photo);
          }
          handleNextItem();
        }}
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
          identity_id: allowMultiIdentity ? undefined : identity?.id,
        }}
        mappings={
          isAllPhotos
            ? undefined
            : [
                {
                  id: 'allPhotos',
                  label: 'All Photos',
                  path: 'all_photos',
                },
                {
                  id: 'matches',
                  label: 'Matches',
                  path: allowMultiIdentity
                    ? 'matched_photos_extended'
                    : 'identities[0].matched_photos_extended',
                },
              ]
        }
        uploadDisabled
        watermarkSettings={watermarkSettings}
      />
    </DesignEditor>
  );
};

PrintProductGroupConfigurator.propTypes = propTypes;

export default PrintProductGroupConfigurator;
